import { forwardRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { buttonVariantPropType, sizePropType, iconPositionPropType, buttonTypePropType } from "../../../propTypes";

/**
 * Unified Button component with consistent variants, sizes, and states
 * Replaces inconsistent button implementations across the application
 *
 * @component
 * @param {Object} props
 * @param {('primary'|'secondary'|'dark'|'outline-primary'|'outline-secondary'|'ghost'|'pill'|'primary-bold'|'cookie-outline'|'cookie-accept')} [props.variant='primary'] - Button visual style
 * @param {('sm'|'md'|'lg')} [props.size='md'] - Button size
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {boolean} [props.loading=false] - Show loading state
 * @param {boolean} [props.fullWidth=false] - Make button full width
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} [props.icon] - Optional icon element
 * @param {('left'|'right')} [props.iconPosition='left'] - Icon position relative to text
 * @param {('button'|'submit'|'reset')} [props.type='button'] - Button type attribute
 * @param {Function} [props.onClick] - Click handler
 * @param {React.ReactNode} props.children - Button content
 */
const Button = forwardRef(
  (
    {
      variant = "primary",
      size = "md",
      disabled = false,
      loading = false,
      fullWidth = false,
      className,
      icon,
      iconPosition = "left",
      type = "button",
      onClick,
      children,
      ...rest
    },
    ref
  ) => {
    const buttonClasses = clsx(
      "btn",
      {
        // Variant classes
        "btn-primary": variant === "primary",
        "btn-secondary": variant === "secondary",
        "btn-dark": variant === "dark",
        "btn-outline-primary": variant === "outline-primary",
        "btn-outline-secondary": variant === "outline-secondary",
        "btn-ghost": variant === "ghost",
        "btn-pill": variant === "pill",
        "btn-primary-bold": variant === "primary-bold",
        "btn-cookie-outline": variant === "cookie-outline",
        "btn-cookie-accept": variant === "cookie-accept",

        // Size classes
        "btn-sm": size === "sm",
        "btn-lg": size === "lg",

        // State classes
        "btn-loading": loading,
        "btn-disabled": disabled || loading,
        "btn-full-width": fullWidth,
      },
      className
    );

    const handleClick = (e) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        {...rest}
      >
        {loading && (
          <span className="btn-spinner" aria-hidden="true">
            <span className="spinner-border spinner-border-sm" role="status" />
          </span>
        )}
        {!loading && icon && iconPosition === "left" && <span className="btn-icon btn-icon-left">{icon}</span>}
        {!loading && children && <span className="btn-content">{children}</span>}
        {!loading && icon && iconPosition === "right" && <span className="btn-icon btn-icon-right">{icon}</span>}
      </button>
    );
  }
);

Button.displayName = "Button";

Button.propTypes = {
  variant: buttonVariantPropType,
  size: sizePropType,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.node,
  iconPosition: iconPositionPropType,
  type: buttonTypePropType,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

export default Button;
